import React, { useState, useEffect, useLayoutEffect, useRef, useContext, useMemo, useCallback } from "react";
import {
  Box, Text, Flex, Menu, MenuButton, MenuList, MenuItem, Icon,
  IconButton, Tooltip, Textarea, Progress, Avatar,
  Modal, ModalOverlay, ModalContent, ModalCloseButton, useToast, Spinner,
} from "@chakra-ui/react";
import { ArrowBackIcon, AttachmentIcon, CloseIcon, CheckIcon, DownloadIcon } from "@chakra-ui/icons";
import { sendFileMessage, setMessageHeart, updateMessageReadTime } from "services/firebaseService";
import { conversationApi } from "services/api/conversations";
import type { Campaign, Client, Collab } from "@foodfluence/shared";
import { canReactToMessage, hasReactionFrom, isSystemNote, systemNoteBody } from "@foodfluence/shared";
import { Message } from "types";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Timestamp } from "firebase/firestore";
import { ConversationsContext } from "contexts/ConversationsDataContext";
import { InfluencerDataContext } from "contexts/InfluencerDataContext";
import { CollabDataLoadingContext } from "contexts/collabDataContext";
import { useMarketplaceOrders } from "contexts/MarketplaceOrdersContext";
import { getInfluencerPrimaryHandle } from "utils/influencerUtils";
import { useConversationEntities } from "../conversationEntities";
import { FiMoreVertical, FiSend } from "react-icons/fi";
import { RiRobot2Line } from "react-icons/ri";
import { Pill } from "components/ui";
import { ff } from "theme";

import { messageSentToClientEmail } from "services/flaskService";
import { processFAQMessage, recordPreVisitCheckInResponse, recordShopifyArrivalCheckInResponse, recordCancelIntentResponse } from "services/api/inbox";
import { checkInChipsStillOpen, isPackageAction, isShopifyArrivalCheckInClosed, isShopifyArrivalStage } from "features/inbox/checkInChipUtils";
import type { CancelIntentAction, CancelIntentOption, CheckInResponseAction } from "@foodfluence/shared";
import { CREATOR_UNDO_WINDOW_MS, isInfluencerCancellableStatus, isWithinInfluencerCancelCutoff } from "@foodfluence/shared";
import ConfirmationModal from "features/collabs/components/ConfirmationModal";
import { submissionPathForCollab } from 'features/collabs/utils/collabRoutes';
import CheckInChips from "features/inbox/components/CheckInChips";
import CancelIntentChips from "features/inbox/components/CancelIntentChips";
import OfferCard from "features/inbox/components/OfferCard";
import MessageHeartBadge from "features/inbox/components/MessageHeartBadge";
import MessageHeartButton from "features/inbox/components/MessageHeartButton";
import { useDoubleTap } from "features/inbox/useDoubleTap";
import CounterReasonModal from "features/collabs/components/counter-flow/CounterReasonModal";
import DeclineReasonModal from "features/collabs/components/counter-flow/DeclineReasonModal";
import CounterOfferModal from "features/collabs/components/counter-flow/CounterOfferModal";
import { collabApi } from "services/api/collab";
import {
  canCreatorStartThread,
  cardsBetween,
  deriveOfferCards,
  getCreatorOfferAvailability,
  getThreadMenuActions,
  OfferCard as OfferCardData,
} from "features/inbox/preCollab";
import { useCampaignSpots } from "hooks/useCampaignSpots";
import { fetchSchedulingCapacityStatus } from "hooks/useSchedulingAvailability";
import { isPreAcceptance } from "utils/collabUtils";
import {
  formatConversationThreadDateSeparator,
  formatMessageTimestamp,
  getConversationFileMediaKind,
  getLatestReadMessageId,
  getThreadReadAction,
  millisOf,
  resolveCreatorArchivedConversations,
  sanitizeMessageHtml,
} from "features/inbox/utils";
import { uploadWithProgress } from "utils/fileUtils";

interface GroupedMessage {
  key: string;
  message: Message;
  isFirst: boolean;
  isLast: boolean;
}

function groupMessages(entries: [string, Message][]): GroupedMessage[] {
  return entries.map(([key, message], index) => {
    const prev = index > 0 ? entries[index - 1][1] : null;
    const next = index < entries.length - 1 ? entries[index + 1][1] : null;
    const isFirst = !prev || prev.sender !== message.sender;
    const isLast = !next || next.sender !== message.sender;
    return { key, message, isFirst, isLast };
  });
}

/** Short clock time for a system note, e.g. "10:24 AM". */
function formatNoteTime(ts?: { seconds: number; nanoseconds: number }): string {
  if (!ts) return "";
  return new Timestamp(ts.seconds, ts.nanoseconds)
    .toDate()
    .toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/**
 * A collab-event note. Rendered as an inline italic line rather than a bubble or a pill: it is
 * nobody's message, and a pill read as another day divider. Mirrors the brand inbox so the same
 * note looks the same on both sides of the thread.
 *
 * `onView` adds a "View" affordance for the kinds that have somewhere to go. A callback, not an
 * href, because the destination differs per app — this inbox opens the creator's collab details,
 * the brand's opens the campaign page — so the route belongs to the caller.
 */
function SystemNote({ body, time, onView }: { body: string; time: string; onView?: () => void }) {
  return (
    <Flex position="relative" align="center" justify="center" gap="6px" my="16px" px="12px">
      <Text
        fontSize="13px"
        fontStyle="italic"
        color={ff.text2}
        textAlign="center"
        maxWidth="80%"
        lineHeight="1.4"
      >
        ✨ {body}
      </Text>
      {onView && (
        <Text
          as="button"
          type="button"
          fontSize="13px"
          fontWeight={600}
          color={ff.ink}
          textDecoration="underline"
          flexShrink={0}
          onClick={onView}
        >
          View
        </Text>
      )}
      {time && (
        <Text position="absolute" right="12px" fontSize="12px" color={ff.muted} flexShrink={0}>
          {time}
        </Text>
      )}
    </Flex>
  );
}

/** Centered pill marking a change of day. */
function ThreadDivider({ children }: { children: React.ReactNode }) {
  return (
    <Flex justify="center" my="14px" px="16px">
      <Box bg={ff.grey} borderRadius="full" px="12px" py="4px" maxWidth="90%">
        <Text fontFamily="display" fontSize="11px" fontWeight={600} color={ff.text2} textAlign="center">
          {children}
        </Text>
      </Box>
    </Flex>
  );
}

/** One-line scrollHeight baseline so auto-grow does not jump to two rows on first keystroke. */
function ensureComposerSingleLineScrollHeight(el: HTMLTextAreaElement, baselineRef: React.MutableRefObject<number | null>): number {
  if (baselineRef.current !== null) return baselineRef.current;
  const saved = el.value;
  el.style.height = "auto";
  el.value = "x";
  baselineRef.current = el.scrollHeight;
  el.value = saved;
  return baselineRef.current;
}

function syncComposerTextareaHeight(el: HTMLTextAreaElement, baselineRef: React.MutableRefObject<number | null>): void {
  const maxPx = typeof window !== "undefined" && window.innerWidth >= 768 ? 200 : 150;
  const baseline = ensureComposerSingleLineScrollHeight(el, baselineRef);

  el.style.height = "auto";
  const contentHeight = el.scrollHeight;
  const threshold = baseline + 2;

  if (contentHeight <= threshold) {
    el.style.height = `${baseline}px`;
    el.style.overflowY = "hidden";
  } else {
    el.style.height = `${Math.min(contentHeight, maxPx)}px`;
    el.style.overflowY = contentHeight > maxPx ? "auto" : "hidden";
  }
}

function getBubbleRadius(isSender: boolean, isFirst: boolean, isLast: boolean): string {
  // Sent bubbles always fully round
  if (isSender) return "20px";
  // Received: small corner on the left side where consecutive msgs connect
  if (isFirst && isLast) return "20px";
  const tl = isFirst ? "20px" : "6px";
  const bl = isLast ? "20px" : "6px";
  return `${tl} 20px 20px ${bl}`;
}

type MessagesLocationState = {
  conversationId: string;
  campaign?: Campaign;
  collab?: Collab;
  client?: Client;
  marketplaceOrderId?: string;
};

const MessagesPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { conversationId: conversationIdParam } = useParams<{ conversationId?: string }>();
  const state = location.state as MessagesLocationState | null;
  const conversationId = conversationIdParam ?? state?.conversationId;
  const conversations = useContext(ConversationsContext);
  const { collab, campaign, client, marketplaceOrderId, isResolved } = useConversationEntities(conversationId, state);
  const collabsLoading = useContext(CollabDataLoadingContext);
  const { loading: ordersLoading } = useMarketplaceOrders();
  const entitySourcesLoading = collabsLoading || ordersLoading;
  const { influencerData, isSwitchedAccount } = useContext(InfluencerDataContext);
  // The account mode captured at mount is the identity whose conversation data
  // this thread renders. If the mode changes while mounted (impersonation
  // started or ended), the mounted messages still belong to the previous
  // identity until the contexts refetch — so leave the thread rather than act
  // on data from the wrong account.
  const mountedModeRef = useRef(isSwitchedAccount);
  useEffect(() => {
    if (isSwitchedAccount !== mountedModeRef.current) {
      navigate("/inbox");
    }
  }, [isSwitchedAccount, navigate]);
  const influencerHandle = getInfluencerPrimaryHandle(influencerData);
  const clientIdForNotifications = campaign?.clientID ?? client?.id ?? "";
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const textareaSingleLineScrollHeightRef = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [message, setMessage] = useState('');
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [latestReadMessageId, setLatestReadMessageId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const [lightbox, setLightbox] = useState<{ url: string; type: 'image' | 'video' } | null>(null);
  const [checkInSubmitting, setCheckInSubmitting] = useState<string | null>(null);
  const [checkInSubmittingAction, setCheckInSubmittingAction] = useState<CheckInResponseAction | null>(null);
  const [pendingCancel, setPendingCancel] = useState<{ key: string; label: string } | null>(null);
  /** Cancel chip awaiting confirmation in the modal (distinct from the check-in cancel above). */
  const [pendingIntentCancel, setPendingIntentCancel] = useState<{ key: string; label: string } | null>(null);
  const [cancelIntentSubmitting, setCancelIntentSubmitting] = useState<string | null>(null);
  const [cancelIntentSubmittingAction, setCancelIntentSubmittingAction] = useState<CancelIntentAction | null>(null);
  // Nothing arrives to re-render the thread when the undo window lapses, so drive it off a clock.
  // Ticks only while an undo is actually pending — see the effect below — so an idle thread and a
  // long-settled cancellation both stay idle.
  const [undoNowMs, setUndoNowMs] = useState<number>(() => Date.now());
  const [isCounterModalOpen, setIsCounterModalOpen] = useState(false);
  /** Reason picked in the counter/decline reason step; set = the in-chat counter form is open. */
  const [counterFormReason, setCounterFormReason] = useState<string | null>(null);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const toast = useToast();
  const onBubbleTap = useDoubleTap();
  /** Messages with a heart write in flight; a fast second double tap would otherwise send the same toggle twice. */
  const heartPendingRef = useRef(new Set<string>());

  const toggleHeart = async (key: string, hearted: boolean) => {
    if (!conversationId || heartPendingRef.current.has(key)) return;
    heartPendingRef.current.add(key);
    try {
      await setMessageHeart(conversationId, key, hearted);
    } catch (error) {
      console.error("Error updating heart:", error);
      toast({ title: "Couldn't update your heart", status: "error", duration: 3000 });
    } finally {
      heartPendingRef.current.delete(key);
    }
  };

  // Milliseconds, not whole seconds: the visibility check below builds its instant from the full
  // Timestamp, so a deadline truncated to the second could stop the clock up to 999ms before the
  // chip was due to go — and nothing else re-renders the thread to finish the job.
  const collabCanceledAtMs = collab?.status === 'collabCanceled' && collab?.collabCanceledTime
    ? new Timestamp(collab.collabCanceledTime.seconds, collab.collabCanceledTime.nanoseconds).toDate().getTime()
    : null;
  useEffect(() => {
    if (collabCanceledAtMs === null) return;
    // The clock exists only to retire the Undo chip on time, so it stops once that has happened.
    // Keyed on the cancel timestamp alone, it ran a re-render every second for as long as any
    // canceled collab was open — days after the 30-minute window had closed and there was
    // nothing left for it to change.
    const deadlineMs = collabCanceledAtMs + CREATOR_UNDO_WINDOW_MS;
    if (Date.now() > deadlineMs) return;
    const id = setInterval(() => {
      const now = Date.now();
      // Published before stopping, so the last tick is the one that flips undoStillOpen false.
      setUndoNowMs(now);
      if (now > deadlineMs) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [collabCanceledAtMs]);

  let messages: Record<string, Message> =
    conversationId != null ? conversations[conversationId]?.messages_log || {} : {};

  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const isInitialScrollRef = useRef(true);

  useEffect(() => {
    if (!conversationId) return;
    const latest = getLatestReadMessageId(conversations[conversationId]?.messages_log);
    setLatestReadMessageId(latest);
  }, [conversationId, messages, conversations]);

  useEffect(() => {
    if (!conversationId) return;
    const initial = isInitialScrollRef.current;
    isInitialScrollRef.current = false;
    requestAnimationFrame(() => {
      if (initial) {
        messagesEndRef.current?.scrollIntoView();
      } else {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    });
    // Mid-transition the messages still belong to the previous identity;
    // the navigation effect above is already exiting the thread.
    if (isSwitchedAccount !== mountedModeRef.current) return;
    const readAction = getThreadReadAction({
      messages,
      isMarketplaceThread: !!marketplaceOrderId,
      isSwitchedAccount,
    });
    if (readAction.kind === "marketplace") {
      conversationApi.markMarketplaceRead({ conversationId }).catch(console.error);
    } else if (readAction.kind === "collab") {
      readAction.messageIds.forEach((key) => {
        updateMessageReadTime(conversationId, key);
      });
    }
  }, [conversationId, Object.keys(messages).length, marketplaceOrderId, isSwitchedAccount]);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    syncComposerTextareaHeight(el, textareaSingleLineScrollHeightRef);
  }, [message, attachedFile]);

  // Prevent the body from being dragged/overscrolled while this page is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Sync container size to the visual viewport so the input rises with the keyboard
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const sync = () => {
      const el = containerRef.current;
      if (!el) return;
      el.style.height = `${vv.height}px`;
      el.style.top = `${vv.offsetTop}px`;
    };
    vv.addEventListener('resize', sync);
    vv.addEventListener('scroll', sync);
    sync();
    return () => {
      vv.removeEventListener('resize', sync);
      vv.removeEventListener('scroll', sync);
    };
  }, []);

  useEffect(() => {
    if (!conversationId) {
      navigate('/inbox', { replace: true });
    }
  }, [conversationId, navigate]);

  // Deep link to an id that isn't ours (or no longer exists): quiet redirect to the list.
  // The loading flags lie on a cold load — CollabDataContext reports "not loading" while the
  // influencer identity is still resolving, and the orders context initializes to false — so
  // only redirect once the unresolved state has PERSISTED, not on the first settled-looking frame.
  useEffect(() => {
    if (!conversationId || isResolved || entitySourcesLoading) {
      return;
    }
    const timer = setTimeout(() => navigate('/inbox', { replace: true }), 4000);
    return () => clearTimeout(timer);
  }, [conversationId, isResolved, entitySourcesLoading, navigate]);

  // --- Pre-collab messaging ---
  // A campaign thread may not exist yet (creator opened it from an invite); the snapshot listener
  // picks the doc up once the first send creates it server-side. Computed before the early returns
  // so the card handlers can be memoized and `memo(OfferCard)` holds.
  const actingInfluencerId = isSwitchedAccount ? influencerData?.id : undefined;
  const threadExists = !!conversationId && !!conversations[conversationId];
  const isMarketplaceThread = !!marketplaceOrderId && !collab;
  const canCompose = isMarketplaceThread || threadExists || canCreatorStartThread(collab);
  const isArchived =
    threadExists && resolveCreatorArchivedConversations({ [conversationId!]: conversations[conversationId!] }).has(conversationId!);
  const offerCards = useMemo(
    () => (isMarketplaceThread ? [] : deriveOfferCards(collab, campaign)),
    [isMarketplaceThread, collab, campaign],
  );
  // Full / ended campaigns turn Accept into the waitlist / extension request ReviewCollabPage opens.
  const spotsCampaignId =
    collab && isPreAcceptance(collab) && campaign?.maxCollabs != null ? collab.influencerCampaignID : undefined;
  const { spots: threadSpots, loading: spotsLoading } = useCampaignSpots(
    spotsCampaignId ? [spotsCampaignId] : [],
    `${collab?.status}:${campaign?.maxCollabs}`,
    collab?.id,
  );
  // In-store open-hours campaigns: every time slot taken also turns Accept into an extension request.
  const [slotsBooked, setSlotsBooked] = useState(false);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const slotsCollab = collab && isPreAcceptance(collab) ? collab : undefined;
  useEffect(() => {
    setSlotsBooked(false);
    setSlotsLoading(false);
    if (!slotsCollab || !campaign) return;
    let cancelled = false;
    setSlotsLoading(true);
    // Never rejects: a failed lookup resolves as not booked.
    fetchSchedulingCapacityStatus(campaign, slotsCollab, slotsCollab.selectedLocation).then((status) => {
      if (cancelled) return;
      setSlotsBooked(status.schedulingFullyBooked);
      setSlotsLoading(false);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotsCollab?.id, slotsCollab?.status, slotsCollab?.selectedLocation, campaign?.id]);
  // Until both checks answer, the card holds Accept / Counter rather than offer what may be refused.
  const offerAvailability = spotsLoading || slotsLoading
    ? 'checking'
    : getCreatorOfferAvailability(collab, campaign, spotsCampaignId ? threadSpots[spotsCampaignId] : undefined, slotsBooked);
  const menuActions = getThreadMenuActions({
    collab, campaign, isMarketplace: isMarketplaceThread, threadExists, isArchived, availability: offerAvailability,
  });
  // The card offered a waitlist / extension request: ReviewCollabPage must not accept if a spot
  // opened in the meantime.
  const expectRequest = offerAvailability !== 'open' && offerAvailability !== 'spotOpened' && offerAvailability !== 'checking';

  // Accept runs the invite page's own accept flow (spots, extension, product selection, and the
  // scheduling / shipping / retail / brand-managed branches) — too page-bound to lift into a modal.
  const offerCollabId = collab?.id;
  const handleAcceptOffer = useCallback(() => {
    if (!offerCollabId) return;
    navigate(`/collabs/review-collab/${offerCollabId}`, {
      state: expectRequest ? { autoAccept: true, expect: 'request' } : { autoAccept: true },
    });
  }, [offerCollabId, navigate, expectRequest]);
  const canSendCounter = menuActions.sendCounter;
  const offerActions = useMemo(() => ({
    onAccept: handleAcceptOffer,
    onCounter: canSendCounter ? () => setIsCounterModalOpen(true) : undefined,
    onDecline: () => setIsDeclineModalOpen(true),
  }), [handleAcceptOffer, canSendCounter]);

  if (!conversationId) {
    return null;
  }

  if (!isResolved) {
    return (
      <Flex height="100dvh" align="center" justify="center">
        <Spinner size="lg" color={ff.ink} />
      </Flex>
    );
  }

  const handleViewCollabOrOrder = () => {
    if (collab?.id) {
      navigate(`/collabs/collab-details/${collab.id}`);
      return;
    }
    if (marketplaceOrderId) {
      navigate(`/collabs/marketplace-orders/${marketplaceOrderId}/upload`);
    }
  };

  const handleDeclineOffer = async (reason: string) => {
    if (!collab) return;
    try {
      await collabApi.declineCollab({ collabId: collab.id, reason });
      toast({ title: 'Collab rejected', status: 'success', duration: 3000, isClosable: true });
    } catch (error) {
      console.error('Error rejecting collab:', error);
      toast({ title: 'Error rejecting collab', status: 'error', duration: 3000, isClosable: true });
    } finally {
      setIsDeclineModalOpen(false);
    }
  };

  const openCounterForm = (reason: string) => {
    setIsCounterModalOpen(false);
    setCounterFormReason(reason);
  };

  const renderOfferCards = (cards: OfferCardData[]) =>
    cards.map((card) => (
      <OfferCard
        key={`offer-${card.key}`}
        card={card}
        brandName={client?.accountName}
        actions={card.turn === 'respond' ? offerActions : undefined}
        availability={card.turn ? offerAvailability : undefined}
      />
    ));

  const handleEditProposal = () => {
    if (!collab) return;
    navigate('/collabs/request-collab', { state: { requestedCollab: collab, campaign, client: { id: client?.id, ...client } } });
  };

  const handleToggleArchive = async () => {
    if (!menuActions.archive || isArchiving) return;
    const archiving = menuActions.archive === 'archive';
    setIsArchiving(true);
    try {
      await conversationApi.setArchived(conversationId, archiving, actingInfluencerId);
      toast({
        title: archiving ? 'Chat archived' : 'Chat unarchived',
        description: archiving ? 'It will come back if the brand replies.' : undefined,
        status: 'success', duration: 3000, isClosable: true,
      });
      if (archiving) navigate('/inbox');
    } catch (error) {
      console.error('Error updating archive state:', error);
      toast({ title: 'Something went wrong', description: 'Please try again.', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsArchiving(false);
    }
  };

  const handleReportUser = () => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:show']);
      window.$crisp.push(['do', 'chat:open']);
    }
  };

  const handleNeedHelp = () => {
    if (window.$crisp) {
      window.$crisp.push(['do', 'chat:show']);
      window.$crisp.push(['do', 'chat:open']);
    }
  };

  const handleFileSelect = async (file: File) => {
    try {
      setAttachedFile(file);
      setMessage(file.name);
      setIsUploading(true);
      setUploadedFileUrl(null);
      const randomKey = Math.random().toString(36).substring(2, 15);
      const filePath = `conversations/files/${randomKey}/${file.name}`;
      const fileUrl = await uploadWithProgress(file, filePath, (progress) => setUploadProgress(progress));
      setUploadedFileUrl(fileUrl);
    } catch (error) {
      console.error("Error uploading file:", error);
      resetMessageInput();
    } finally {
      setIsUploading(false);
    }
  };

  // Fire-and-forget: the message is already in the thread, the brand email is a courtesy.
  const notifyClientByEmail = () => {
    messageSentToClientEmail(clientIdForNotifications, client?.email ?? "", client?.accountName ?? "", influencerHandle, message, conversationId).catch((error) => console.error("Error emailing the brand about a new message:", conversationId, error));
  };

  const handleSendMessage = async () => {
    // Composer is locked while a pre-visit check-in awaits a pre-filled choice.
    if (checkInAwaitingChoice) return;
    const trimmedMessage = message.trim();
    if (trimmedMessage === '') return;
    setIsSending(true);
    if (attachedFile && uploadedFileUrl) {
      try {
        const messageId = marketplaceOrderId
          ? (await conversationApi.sendMarketplaceFileMessage({ conversationId, fileName: attachedFile.name, fileUrl: uploadedFileUrl }, actingInfluencerId)).messageId
          : await sendFileMessage(conversationId, attachedFile.name, uploadedFileUrl);
        // Marketplace conversations have their own brand notification path (SMS via the
        // web backend) — this Flask email is for collab conversations only.
        if (!marketplaceOrderId) {
          notifyClientByEmail();
        }
        processFAQMessage(conversationId, attachedFile.name, messageId).catch(console.error);
        resetMessageInput();
      } catch (error) {
        console.error("Error sending file:", error);
        toast({ title: "File not sent", description: "Please try again.", status: "error", duration: 4000, isClosable: true });
      } finally {
        setIsSending(false);
      }
    } else {
      try {
        const response = marketplaceOrderId
          ? await conversationApi.sendMarketplaceMessage({ conversationId, message }, actingInfluencerId)
          : await conversationApi.sendMessage({ conversationId, message }, actingInfluencerId);
        const messageId = response.messageId;
        // Marketplace conversations have their own brand notification path (SMS via the
        // web backend) — this Flask email is for collab conversations only.
        if (!marketplaceOrderId) {
          notifyClientByEmail();
        }
        processFAQMessage(conversationId, message, messageId).catch(console.error);
        resetMessageInput();
      } catch (error) {
        console.error("Error sending message:", error);
        // 403 while starting a campaign thread: the invite was accepted or declined meanwhile, so a
        // retry can't succeed.
        const inviteClosed = !marketplaceOrderId && !threadExists && (error as { status?: number })?.status === 403;
        toast({
          title: "Message not sent",
          description: inviteClosed ? "This invite is no longer open." : "Please try again.",
          status: "error", duration: 4000, isClosable: true,
        });
      } finally {
        setIsSending(false);
      }
    }
  };

  const resetMessageInput = () => {
    setAttachedFile(null);
    setMessage('');
    setUploadProgress(0);
    setUploadedFileUrl(null);
  };

  const submitCheckInResponse = async (
    action: CheckInResponseAction,
    label: string,
    msgKey: string,
  ) => {
    if (!collab?.id || checkInSubmitting) return;
    setCheckInSubmitting(msgKey);
    setCheckInSubmittingAction(action);
    try {
      // Echo the creator's choice into the thread, then record the response server-side.
      await conversationApi.sendMessage({ conversationId, message: label }, actingInfluencerId);
      const packageAnswer = isPackageAction(action);
      const { result } = packageAnswer
        ? await recordShopifyArrivalCheckInResponse(collab.id, action)
        : await recordPreVisitCheckInResponse(collab.id, action);

      if (result === 'navigateToReschedule') {
        navigate('/collabs/schedule-collab', {
          state: { collabId: collab.id, campaign, influencer: influencerData },
        });
        return;
      }
      if (result === 'navigateToContentSubmission') {
        // Creator confirmed they already visited — the dashboard advanced the collab to content
        // submission (keeping the original date); send them to submit content.
        navigate(submissionPathForCollab(collab));
        return;
      }
      if (result === 'canceled') {
        toast({ title: 'Collaboration canceled', status: 'info', duration: 4000, isClosable: true });
      } else if (result === 'alertRaised' && !packageAnswer) {
        // (A package answer that alerted our team gets the brand's reply in the thread instead.)
        toast({
          title: 'Reschedule request sent',
          description: "We've let the brand know you'd like to reschedule and they'll be in touch.",
          status: 'success', duration: 5000, isClosable: true,
        });
      } else if (result === 'confirmed') {
        toast({ title: 'Thanks for confirming!', status: 'success', duration: 3000, isClosable: true });
      }
    } catch (error) {
      console.error('Error recording check-in response:', error);
      toast({ title: 'Something went wrong', description: 'Please try again.', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setCheckInSubmitting(null);
      setCheckInSubmittingAction(null);
    }
  };

  // Cancel is destructive, so confirm via a modal before actually canceling.
  const handleCheckInChip = (
    action: CheckInResponseAction,
    label: string,
    msgKey: string,
  ) => {
    if (checkInSubmitting) return;
    if (action === 'cancel') {
      setPendingCancel({ key: msgKey, label });
      return;
    }
    void submitCheckInResponse(action, label, msgKey);
  };

  const confirmCancelCheckIn = async () => {
    if (!pendingCancel) return;
    await submitCheckInResponse('cancel', pendingCancel.label, pendingCancel.key);
    setPendingCancel(null);
  };

  /**
   * Answer the cancel agent's confirmation prompt.
   *
   * No extra confirm modal here, unlike the check-in cancel chip: this message IS the
   * confirmation step, and a second "are you sure?" on top of "should I cancel?" reads as a
   * bug. The safe choice is already the primary button.
   */
  const submitCancelIntentResponse = async (
    action: CancelIntentAction,
    label: string,
    msgKey: string,
  ) => {
    if (!collab?.id || cancelIntentSubmitting) return;
    // Cancel is the one irreversible choice on the prompt, so it routes through a modal first.
    // The others act immediately: rescheduling opens a negotiation the creator can abandon, and
    // "keep it as is" changes nothing.
    if (action === 'confirmCancel') {
      setPendingIntentCancel({ key: msgKey, label });
      return;
    }
    await runCancelIntentResponse(action, label, msgKey);
  };

  /** The actual call, past any confirmation. */
  const runCancelIntentResponse = async (
    action: CancelIntentAction,
    label: string,
    msgKey: string,
  ) => {
    if (!collab?.id || cancelIntentSubmitting) return;
    setCancelIntentSubmitting(msgKey);
    setCancelIntentSubmittingAction(action);
    try {
      // Record BEFORE echoing. The echo is cosmetic; the recording is the action. Echoing first
      // meant a failed recording still put "Yes, cancel" in the thread, telling the brand the
      // creator answered something that never took effect.
      const { result } = await recordCancelIntentResponse(collab.id, action, msgKey);
      // Then echo the creator's choice, matching the check-in chip path, so the brand sees the
      // answer in the creator's own voice rather than only the agent's reply.
      //
      // Its own catch, and deliberately not fatal: past this line the collab really is canceled.
      // Letting a failed echo fall into the outer catch told the creator "Something went wrong,
      // please try again" about an action that had already succeeded — so their next move was to
      // tap again and get "already been handled". A missing echo costs the brand one line of
      // thread context; a false failure costs the creator their understanding of what happened.
      // Only for answers that took effect. A 'noop' means the prompt was stale, superseded, or
      // already handled — echoing there put "Cancel collab" in the thread as though the creator
      // had said it, showing the brand a decision that never happened.
      if (result !== 'noop') {
        await conversationApi.sendMessage({ conversationId, message: label }, actingInfluencerId).catch((echoError) => {
          console.error('Cancel-intent response recorded, but echoing it to the thread failed:', echoError);
        });
      }

      if (result === 'canceled') {
        toast({ title: 'Collaboration canceled', status: 'info', duration: 4000, isClosable: true });
      } else if (result === 'rescheduling') {
        // Straight into the app's own scheduling UI rather than a conversational back-and-forth:
        // the creator picks from the real bookable slots instead of proposing times the agent then
        // has to validate. The server call above still happened — it closes the cancel session so
        // the chips retire and the prompt cannot be answered twice. Same navigation the check-in
        // reschedule chip uses.
        navigate('/collabs/schedule-collab', {
          state: { collabId: collab.id, campaign, influencer: influencerData },
        });
        return;
      } else if (result === 'undone') {
        toast({ title: 'Collaboration restored', status: 'success', duration: 4000, isClosable: true });
      } else if (result === 'noop') {
        toast({
          title: 'This request has already been handled',
          description: 'Reach out to the Foodfluence team if you still need help.',
          status: 'info', duration: 5000, isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error recording cancel-intent response:', error);
      toast({ title: 'Something went wrong', description: 'Please try again.', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setCancelIntentSubmitting(null);
      setCancelIntentSubmittingAction(null);
    }
  };

  const sortedEntries = messages
    ? Object.entries(messages).sort(([, a], [, b]) => {
        const ta = new Timestamp(a?.time_sent?.seconds, a?.time_sent?.nanoseconds);
        const tb = new Timestamp(b?.time_sent?.seconds, b?.time_sent?.nanoseconds);
        return ta.toDate().getTime() - tb.toDate().getTime();
      })
    : [];

  const grouped = groupMessages(sortedEntries);
  let lastDateLabel = "";

  // Latest creator reply time — chips hide once the creator has responded to a check-in.
  const lastInfluencerSentMs = sortedEntries.reduce((max, [, m]) => {
    if (m.sender === 'influencer' && m.time_sent) {
      const ms = new Timestamp(m.time_sent.seconds, m.time_sent.nanoseconds).toDate().getTime();
      return Math.max(max, ms);
    }
    return max;
  }, 0);

  const preVisitResolved =
    collab?.preVisitCheckInStatus === 'confirmed' ||
    collab?.preVisitCheckInStatus === 'reschedule' ||
    collab?.preVisitCheckInStatus === 'canceled' ||
    collab?.preVisitCheckInStatus === 'autoCanceled';

  // Timestamp of the most recent message that carries chips — only that one shows chips, so an
  // earlier check-in's chips never re-surface (e.g. behind a later uncancel "already visited /
  // reschedule" prompt).
  const latestCheckInSentMs = sortedEntries.reduce((max, [, m]) => {
    if (m.checkInOptions?.length && m.time_sent) {
      const ms = new Timestamp(m.time_sent.seconds, m.time_sent.nanoseconds).toDate().getTime();
      return Math.max(max, ms);
    }
    return max;
  }, 0);

  const shouldShowCheckInChips = (msg: Message): boolean => {
    if (!msg.checkInOptions?.length || preVisitResolved) return false;
    if (isShopifyArrivalStage(msg.checkInStage) && isShopifyArrivalCheckInClosed(collab)) return false;
    const sentMs = msg.time_sent
      ? new Timestamp(msg.time_sent.seconds, msg.time_sent.nanoseconds).toDate().getTime()
      : 0;
    return checkInChipsStillOpen({ stage: msg.checkInStage, sentMs, latestCheckInSentMs, lastInfluencerSentMs });
  };

  // Newest message in the cancel-intent exchange, whether or not it carries chips: the prompt, a
  // nudge that re-posts it, or the agent's closing reply, which carries an EMPTY option list
  // precisely so it supersedes them.
  const latestCancelIntentSentMs = sortedEntries.reduce((max, [, m]) => {
    if (m.cancelIntentOptions && m.time_sent) {
      const ms = new Timestamp(m.time_sent.seconds, m.time_sent.nanoseconds).toDate().getTime();
      return Math.max(max, ms);
    }
    return max;
  }, 0);

  // A terminal collab has nothing left to cancel. Delegated to the shared guard rather than
  // re-typing the status: it is the same rule the agent's eligibility probe and /collabs/cancel
  // run, and its own docstring warns that a second copy drifting means the UI offers an action
  // the API refuses.
  const collabIsCancellable = isInfluencerCancellableStatus(collab?.status);

  // Undo is offered only while the collab is still canceled and still inside the window. Both are
  // re-checked server-side on the tap, so this drives visibility only — never permission.
  const canceledAtMs = collab?.status === 'collabCanceled' && collab?.collabCanceledTime
    ? new Timestamp(collab.collabCanceledTime.seconds, collab.collabCanceledTime.nanoseconds).toDate().getTime()
    : null;
  const undoStillOpen = canceledAtMs !== null && undoNowMs - canceledAtMs <= CREATOR_UNDO_WINDOW_MS;

  /**
   * Chips live on the newest message of the exchange, and only while it still offers them.
   *
   * Deliberately NOT hidden by a creator message the way check-in chips are: the natural answer
   * to "should I cancel it?" is to type "yes", and hiding on that took the buttons away at the
   * exact moment the creator was trying to say yes — leaving them with no way to confirm and no
   * indication that their words hadn't counted. The server nudges them back here instead, and
   * resolves the prompt by posting a reply with an empty option list.
   */
  const shouldShowCancelIntentChips = (msg: Message): boolean => {
    if (!msg.cancelIntentOptions?.length) return false;
    const sentMs = msg.time_sent
      ? new Timestamp(msg.time_sent.seconds, msg.time_sent.nanoseconds).toDate().getTime()
      : 0;
    if (sentMs !== latestCancelIntentSentMs) return false;

    // The Undo chip lives on the cancel confirmation, so it is gated on the OPPOSITE collab state
    // from the confirm/keep pair — and disappears on its own the moment the undo succeeds, because
    // the status goes back to pendingRedemption.
    const isUndo = msg.cancelIntentOptions.some((o) => o.action === 'undoCancel');
    if (isUndo) return undoStillOpen;
    // Still something to offer? The cancel chip can lapse on its own (below) while reschedule
    // does not, so an in-cutoff prompt keeps its Reschedule button rather than vanishing whole.
    return collabIsCancellable && visibleCancelIntentOptions(msg).length > 0;
  };

  /**
   * Which of a prompt's chips are still live.
   *
   * A prompt can outlive one of its own options: the cancel cutoff closes as the visit
   * approaches, and the message carrying the buttons does not change when it does. Cancel was
   * staying tappable past it, answering with a server-side refusal the creator had no way to
   * anticipate. Rescheduling has no such cutoff, so it survives — which is the whole point of
   * offering it, and means the prompt degrades to Reschedule rather than going dead.
   *
   * Visibility only. The server re-derives both rules on the tap and is the authority; this just
   * stops us showing a button we already know will be refused.
   */
  const visibleCancelIntentOptions = (msg: Message): CancelIntentOption[] => {
    const options = msg.cancelIntentOptions ?? [];
    const visitDate = collab?.scheduledCollabDate
      ? new Timestamp(collab.scheduledCollabDate.seconds, collab.scheduledCollabDate.nanoseconds).toDate()
      : null;

    // Only the cancel chip is filtered here, and only on the visit date. That check survives a
    // stale collab because it is about time passing, not about the collab changing: the date it
    // reads is the same date the prompt was built from, and all that moves is `now`.
    //
    // Reschedule is deliberately NOT filtered. The server decided whether to offer it, from a
    // fresh read, at prompt time — re-deciding here duplicates that against `collab`, which is a
    // snapshot frozen when the thread was opened (see conversationEntities: `state?.collab`
    // wins over the live list). A collab edited since then still carries its old visit date,
    // which hid the chip while the server was correctly offering it. If eligibility does lapse
    // between the prompt and the tap, the scheduling screen refuses with a reason the creator can
    // read — a far better outcome than a button that silently is not there.
    return options.filter((o) =>
      o.action === 'confirmCancel' ? !isWithinInfluencerCancelCutoff(visitDate) : true,
    );
  };

  // While a pre-visit check-in is awaiting a response, lock free-text input so the
  // creator must pick a pre-filled option (those have actionable consequences wired up).
  const checkInAwaitingChoice = sortedEntries.some(([, m]) => shouldShowCheckInChips(m));

  return (
    <Box
      ref={containerRef}
      bg="white"
      position="fixed"
      top="0"
      left="0"
      right="0"
      height="100svh"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      {/* Header */}
      <Flex
        align="center"
        justify="space-between"
        px="4px"
        minH="68px"
        borderBottom="1px solid"
        borderColor={ff.line}
        bg="white"
        color={ff.ink}
        fontFamily="display"
        flexShrink={0}
      >
        <Flex align="center" flex="1" minW={0}>
          <IconButton
            aria-label="Back"
            icon={<ArrowBackIcon boxSize={5} />}
            variant="ghost"
            w="44px"
            h="44px"
            minW="44px"
            color={ff.ink}
            _hover={{ bg: ff.grey }}
            onClick={() => navigate(-1)}
            borderRadius="full"
            flexShrink={0}
          />
          <Avatar
            src={client?.instagramProfilePicture}
            name={client?.accountName}
            w="40px"
            h="40px"
            flexShrink={0}
            bg={ff.grey}
            color={ff.ink}
            fontSize="13px"
            fontWeight={600}
            border="1px solid"
            borderColor={ff.line}
            mr="10px"
          />
          <Box minW={0}>
            <Text
              as="h1"
              fontWeight={600}
              fontSize="16px"
              color={ff.ink}
              lineHeight="1.2"
              noOfLines={1}
            >
              {client?.accountName}
            </Text>
            {campaign?.name && (
              <Text fontSize="12px" fontWeight={600} color={ff.text2} noOfLines={1}>
                {campaign.name}
              </Text>
            )}
          </Box>
        </Flex>

        <Menu placement="bottom-end">
          <MenuButton
            as={IconButton}
            aria-label="Options"
            icon={<FiMoreVertical size={20} />}
            variant="ghost"
            w="44px"
            h="44px"
            minW="44px"
            borderRadius="full"
            color={ff.ink}
            _hover={{ bg: ff.grey }}
            _active={{ bg: ff.line }}
            flexShrink={0}
          />
          <MenuList
            minWidth="200px"
            border="1px solid"
            borderColor={ff.border}
            borderRadius="16px"
            fontFamily="display"
            boxShadow="0px 4px 24px rgba(0,0,0,0.10), 0px 1px 4px rgba(0,0,0,0.06)"
            py="6px"
            px="0"
            overflow="hidden"
            zIndex={9999}
          >
            {(collab?.id || marketplaceOrderId) && (
              <>
                <MenuItem
                  onClick={handleViewCollabOrOrder}
                  fontSize="15px"
                  fontWeight={600}
                  color={ff.ink}
                  px="16px"
                  minH="44px"
                  _hover={{ bg: ff.grey }}
                  _focus={{ bg: ff.grey }}
                >
                  {marketplaceOrderId && !collab?.id ? "View marketplace order" : "View collab details"}
                </MenuItem>
                <Box mx="16px" h="1px" bg={ff.line} />
              </>
            )}
            {menuActions.sendCounter && (
              <>
                <MenuItem onClick={() => setIsCounterModalOpen(true)}
              fontSize="15px"
              fontWeight={600}
              color={ff.ink}
              px="16px"
              minH="44px"
              _hover={{ bg: ff.grey }}
              _focus={{ bg: ff.grey }}
                >
                  Send counter
                </MenuItem>
                <Box mx="16px" h="1px" bg={ff.line} />
              </>
            )}
            {menuActions.editProposal && (
              <>
                <MenuItem onClick={handleEditProposal}
              fontSize="15px"
              fontWeight={600}
              color={ff.ink}
              px="16px"
              minH="44px"
              _hover={{ bg: ff.grey }}
              _focus={{ bg: ff.grey }}
                >
                  Edit proposal
                </MenuItem>
                <Box mx="16px" h="1px" bg={ff.line} />
              </>
            )}
            {menuActions.archive && (
              <>
                <MenuItem onClick={handleToggleArchive} isDisabled={isArchiving}
              fontSize="15px"
              fontWeight={600}
              color={ff.ink}
              px="16px"
              minH="44px"
              _hover={{ bg: ff.grey }}
              _focus={{ bg: ff.grey }}
                >
                  {menuActions.archive === 'archive' ? 'Archive' : 'Unarchive'}
                </MenuItem>
                <Box mx="16px" h="1px" bg={ff.line} />
              </>
            )}
            <MenuItem
              onClick={handleNeedHelp}
              fontSize="15px"
              fontWeight={600}
              color={ff.ink}
              px="16px"
              minH="44px"
              _hover={{ bg: ff.grey }}
              _focus={{ bg: ff.grey }}
            >
              Need help?
            </MenuItem>
            <Box mx="16px" h="1px" bg={ff.line} />
            <MenuItem
              onClick={handleReportUser}
              fontSize="15px"
              fontWeight={600}
              color={ff.danger}
              px="16px"
              minH="44px"
              _hover={{ bg: ff.grey }}
              _focus={{ bg: ff.grey }}
            >
              Report user
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>

      {/* Messages */}
      <Box
        flex={1}
        overflowY="auto"
        px="12px"
        py="12px"
        // `manipulation` drops the browser's double-tap-to-zoom, which would swallow the double tap
        // that hearts a message; pinch zoom still works.
        sx={{ WebkitOverflowScrolling: "touch", overscrollBehavior: "none", touchAction: "manipulation" }}
        bg="white"
      >
        {grouped.length === 0 && (
          <Text textAlign="center" fontSize="14px" color={ff.muted} my="24px">
            No messages yet
          </Text>
        )}
        {grouped.map(({ key, message: msg, isFirst, isLast }, index) => {
          // Offer cards dated after the previous message and up to this one render just before it.
          const prevMsgMs = index > 0 ? millisOf(grouped[index - 1].message.time_sent) : -Infinity;
          const cardsHere = offerCards.length ? cardsBetween(offerCards, prevMsgMs, millisOf(msg.time_sent)) : [];
          const isSender = msg.sender === "influencer";
          const isAI = msg.isAIFAQResponse === true;
          // Only one side can react to any message (never your own), so at most one heart shows.
          const canHeart = canReactToMessage(msg, "influencer");
          const heartedByMe = hasReactionFrom(msg, "influencer");
          const heartedByBrand = hasReactionFrom(msg, "client");
          const toggleMyHeart = () => toggleHeart(key, !heartedByMe);
          // A photo or video opens on a single tap; on a heartable bubble that tap waits out the
          // double-tap window first (see the bubble wrapper below).
          const mediaKind = msg.type === 'file' && msg.file_url ? getConversationFileMediaKind(msg) : null;
          const openMedia = msg.file_url && (mediaKind === 'image' || mediaKind === 'video')
            ? () => setLightbox({ url: msg.file_url!, type: mediaKind })
            : undefined;
          const { formattedString } = formatMessageTimestamp(msg, isSender, key === latestReadMessageId);
          const bubbleRadius = getBubbleRadius(isSender, isFirst, isLast);

          const dateLabel = msg.time_sent ? formatConversationThreadDateSeparator(msg.time_sent) : "";
          const showDateSep = dateLabel && dateLabel !== lastDateLabel;
          if (showDateSep) lastDateLabel = dateLabel;

          // Status notes (e.g. a reschedule) belong to neither party — render them as a
          // centered pill rather than a bubble on one side of the thread.
          if (isSystemNote(msg)) {
            return (
              <React.Fragment key={key}>
                {renderOfferCards(cardsHere)}
                {showDateSep && <ThreadDivider>{dateLabel}</ThreadDivider>}
                <SystemNote
                  // Fixed-copy kinds render from SYSTEM_NOTE_TEXT rather than the stored body, so a
                  // reword reaches old notes too; the rest fall back to what was written.
                  body={systemNoteBody(msg)}
                  time={formatNoteTime(msg.time_sent)}
                  onView={
                    msg.systemNoteKind === 'deliverablesUpdated' && collab?.id
                      ? () => navigate(`/collabs/collab-details/${collab.id}`)
                      : undefined
                  }
                />
              </React.Fragment>
            );
          }

          return (
            <React.Fragment key={key}>
              {renderOfferCards(cardsHere)}
              {showDateSep && <ThreadDivider>{dateLabel}</ThreadDivider>}

              <Flex
                role="group"
                justify={isSender ? "flex-end" : "flex-start"}
                mb={isLast ? "8px" : "5px"}
                width="100%"
              >
                {isAI && (
                  <Avatar
                    src="/sienna-avatar.png"
                    name="Sienna"
                    boxSize="28px"
                    mr="8px"
                    alignSelf="flex-end"
                    flexShrink={0}
                  />
                )}
                <Box
                  maxWidth="76%"
                  display="flex"
                  flexDirection="column"
                  alignItems={isSender ? "flex-end" : "flex-start"}
                >
                  {/* `contents` keeps the bubble laid out as before; the box is only here to catch
                      taps on the bubble itself and not on the chips below it. */}
                  <Box
                    display="contents"
                    onClick={canHeart ? () => onBubbleTap(key, toggleMyHeart, openMedia) : undefined}
                  >
                  {msg.type === 'message' ? (
                    <Box
                      px="14px"
                      py="10px"
                      borderRadius={bubbleRadius}
                      bg={isSender ? ff.ink : isAI ? "white" : ff.grey}
                      border={isAI ? "1px solid" : "none"}
                      borderColor={ff.border}
                      wordBreak="break-word"
                      overflowWrap="break-word"
                    >
                      {isAI && (
                        <Box mb="6px" w="fit-content">
                          <Pill tone="outline">
                            <RiRobot2Line style={{ marginRight: 4 }} />
                            AI assistant
                          </Pill>
                        </Box>
                      )}
                      <Box
                        as="div"
                        fontSize="15px"
                        lineHeight="1.375"
                        color={isSender ? "white" : ff.ink}
                        sx={{
                          "& p": { margin: "0 0 0.5em", "&:last-child": { marginBottom: 0 } },
                          "& a": { color: isSender ? ff.lime : ff.ink, textDecoration: "underline" },
                          "& .ql-indent-1": { paddingLeft: "3em" },
                          "& ol, & ul": { margin: "0.5em 0", paddingLeft: "1.5em" },
                        }}
                        dangerouslySetInnerHTML={{ __html: sanitizeMessageHtml(msg.body) }}
                      />
                    </Box>
                  ) : msg.type === 'file' ? (() => {
                    const fileHref = msg.file_url;
                    if (!fileHref) {
                      return (
                        <Box
                          px="14px"
                          py="10px"
                          borderRadius={bubbleRadius}
                          bg={isSender ? ff.ink : ff.grey}
                          wordBreak="break-word"
                        >
                          <Flex alignItems="center">
                            <AttachmentIcon boxSize={4} mr={2} color={isSender ? "white" : ff.text2} />
                            <Text fontSize="15px" color={isSender ? "white" : ff.ink}>
                              {msg.body}
                            </Text>
                          </Flex>
                        </Box>
                      );
                    }

                    const mediaType = getConversationFileMediaKind(msg);
                    const mediaLetterboxBg = isSender ? ff.inkSoft : ff.grey;

                    if (mediaType === 'image') {
                      return (
                        <Box maxW="260px" alignSelf={isSender ? "flex-end" : "flex-start"}>
                          <Box
                            borderRadius={bubbleRadius}
                            overflow="hidden"
                            bg={mediaLetterboxBg}
                          >
                            <Box
                              as="button"
                              type="button"
                              p={0}
                              m={0}
                              border="none"
                              w="100%"
                              cursor="pointer"
                              bg="transparent"
                              onClick={canHeart ? undefined : () => setLightbox({ url: fileHref, type: 'image' })}
                              aria-label={`View image ${msg.body}`}
                            >
                              <Box
                                as="img"
                                src={fileHref}
                                alt={msg.body}
                                loading="lazy"
                                display="block"
                                w="100%"
                                maxH="320px"
                                objectFit="contain"
                              />
                            </Box>
                          </Box>
                        </Box>
                      );
                    }
                    if (mediaType === 'video') {
                      return (
                        <Box maxW="280px" alignSelf={isSender ? "flex-end" : "flex-start"}>
                          <Box
                            borderRadius={bubbleRadius}
                            overflow="hidden"
                            bg={mediaLetterboxBg}
                            cursor="pointer"
                            onClick={canHeart ? undefined : () => setLightbox({ url: fileHref, type: 'video' })}
                            position="relative"
                          >
                            <video
                              src={fileHref}
                              playsInline
                              muted
                              style={{ display: "block", width: "100%", maxHeight: "320px", verticalAlign: "middle", pointerEvents: "none" }}
                            />
                            {/* Play button overlay */}
                            <Flex
                              position="absolute"
                              inset="0"
                              align="center"
                              justify="center"
                              pointerEvents="none"
                            >
                              <Flex
                                align="center"
                                justify="center"
                                w="44px"
                                h="44px"
                                borderRadius="full"
                                bg="rgba(0,0,0,0.45)"
                                backdropFilter="blur(4px)"
                              >
                                <Box
                                  as="span"
                                  display="block"
                                  w="0"
                                  h="0"
                                  ml="3px"
                                  borderTop="9px solid transparent"
                                  borderBottom="9px solid transparent"
                                  borderLeft="15px solid white"
                                />
                              </Flex>
                            </Flex>
                          </Box>
                        </Box>
                      );
                    }
                    // PDF / other files
                    return (
                      <Box
                        px="14px"
                        py="10px"
                        borderRadius={bubbleRadius}
                        bg={isSender ? ff.ink : ff.grey}
                        wordBreak="break-word"
                      >
                        {isAI && (
                          <Box mb="6px" w="fit-content">
                            <Pill tone="outline">
                              <RiRobot2Line style={{ marginRight: 4 }} />
                              AI assistant
                            </Pill>
                          </Box>
                        )}
                        <Flex alignItems="center">
                          <AttachmentIcon boxSize={4} mr={2} color={isSender ? "white" : ff.text2} />
                          <a href={fileHref} target="_blank" rel="noopener noreferrer">
                            <Text
                              as="span"
                              fontSize="15px"
                              color={isSender ? ff.lime : ff.ink}
                              textDecoration="underline"
                            >
                              {msg.body}
                            </Text>
                          </a>
                        </Flex>
                      </Box>
                    );
                  })() : null}
                  </Box>

                  {heartedByMe && <MessageHeartBadge lovedBy="You" onRemove={() => toggleHeart(key, false)} />}
                  {heartedByBrand && <MessageHeartBadge lovedBy={client?.accountName || "The brand"} />}

                  {!isSender && shouldShowCheckInChips(msg) && (
                    <CheckInChips
                      options={msg.checkInOptions!}
                      sentMs={
                        msg.time_sent
                          ? new Timestamp(msg.time_sent.seconds, msg.time_sent.nanoseconds).toDate().getTime()
                          : Date.now()
                      }
                      checkInStage={msg.checkInStage}
                      submittingKey={checkInSubmitting}
                      submittingAction={checkInSubmittingAction}
                      msgKey={key}
                      onChip={handleCheckInChip}
                    />
                  )}

                  {!isSender && shouldShowCancelIntentChips(msg) && (
                    <CancelIntentChips
                      options={visibleCancelIntentOptions(msg)}
                      submittingKey={cancelIntentSubmitting}
                      submittingAction={cancelIntentSubmittingAction}
                      msgKey={key}
                      onChip={submitCancelIntentResponse}
                    />
                  )}

                  {isLast && (
                    <Flex align="center" mt="4px" gap="3px">
                      {isSender && msg?.time_read && key === latestReadMessageId && (
                        <Tooltip label="Read" aria-label="Read" bg="white" color="black">
                          <CheckIcon color={ff.muted} boxSize="2.5" />
                        </Tooltip>
                      )}
                      <Text fontSize="11px" color={ff.muted}>
                        {formattedString}
                      </Text>
                    </Flex>
                  )}
                </Box>
                {canHeart && <MessageHeartButton hearted={heartedByMe} onToggle={toggleMyHeart} />}
              </Flex>
            </React.Fragment>
          );
        })}
        {offerCards.length > 0 &&
          renderOfferCards(
            cardsBetween(
              offerCards,
              grouped.length ? millisOf(grouped[grouped.length - 1].message.time_sent) : -Infinity,
              Infinity,
            ),
          )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Lightbox */}
      <Modal
        isOpen={lightbox !== null}
        onClose={() => setLightbox(null)}
        isCentered
        size="full"
      >
        <ModalOverlay bg="rgba(0,0,0,0.92)" />
        <ModalContent
          bg="transparent"
          boxShadow="none"
          display="flex"
          alignItems="center"
          justifyContent="center"
          m={0}
          onClick={() => setLightbox(null)}
        >
          <ModalCloseButton
            w="44px"
            h="44px"
            color="white"
            bg="rgba(255,255,255,0.15)"
            borderRadius="full"
            top="16px"
            right="16px"
            zIndex={1}
            _hover={{ bg: "rgba(255,255,255,0.25)" }}
          />
          {lightbox && (
            <Box
              as="a"
              href={lightbox.url}
              download
              target="_blank"
              rel="noopener noreferrer"
              position="absolute"
              top="16px"
              left="16px"
              zIndex={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="44px"
              h="44px"
              borderRadius="full"
              bg="rgba(255,255,255,0.15)"
              color="white"
              _hover={{ bg: "rgba(255,255,255,0.25)" }}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <DownloadIcon boxSize={3.5} />
            </Box>
          )}
          {lightbox?.type === 'image' && (
            <Box
              as="img"
              src={lightbox.url}
              alt=""
              maxW="100vw"
              maxH="100svh"
              objectFit="contain"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            />
          )}
          {lightbox?.type === 'video' && (
            <Box
              as="video"
              src={lightbox.url}
              controls
              autoPlay
              playsInline
              maxW="100vw"
              maxH="100svh"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{ outline: 'none' }}
            />
          )}
        </ModalContent>
      </Modal>

      {/* Cancel confirmation */}
      <ConfirmationModal
        isOpen={pendingCancel !== null}
        onClose={() => { if (!checkInSubmitting) setPendingCancel(null); }}
        onConfirm={confirmCancelCheckIn}
        title="Cancel this collaboration?"
        message={`Are you sure you want to cancel your collaboration${client?.accountName ? ` with ${client.accountName}` : ''}? This can't be undone.`}
        confirmText="Yes, cancel collab"
        isLoading={pendingCancel !== null && checkInSubmitting === pendingCancel.key}
        footerWarning={{ text: 'This will permanently cancel the collaboration.' }}
      />

      {/* Cancel-agent confirmation. Separate from the check-in modal above because the outcome
          differs: this cancel carries a 30-minute Undo, so it must not claim to be permanent. */}
      <ConfirmationModal
        isOpen={pendingIntentCancel !== null}
        onClose={() => { if (!cancelIntentSubmitting) setPendingIntentCancel(null); }}
        onConfirm={async () => {
          if (!pendingIntentCancel) return;
          const { key, label } = pendingIntentCancel;
          setPendingIntentCancel(null);
          await runCancelIntentResponse('confirmCancel', label, key);
        }}
        title="Cancel this collaboration?"
        message={`This will cancel your collaboration${client?.accountName ? ` with ${client.accountName}` : ''} and let the brand know. You'll have a short window to undo it afterwards.`}
        confirmText="Yes, cancel collab"
        isLoading={pendingIntentCancel !== null && cancelIntentSubmitting === pendingIntentCancel.key}
      />

      {collab && campaign && (
        <CounterReasonModal
          isOpen={isCounterModalOpen}
          onClose={() => setIsCounterModalOpen(false)}
          onSubmit={() => setIsCounterModalOpen(false)}
          onBack={() => setIsCounterModalOpen(true)}
          onReasonSelected={openCounterForm}
          collab={collab}
          campaign={campaign}
        />
      )}
      {collab && campaign && (
        <DeclineReasonModal
          isOpen={isDeclineModalOpen}
          onClose={() => setIsDeclineModalOpen(false)}
          onSubmit={handleDeclineOffer}
          onBack={() => setIsDeclineModalOpen(true)}
          onCounter={openCounterForm}
          collab={collab}
          campaign={campaign}
        />
      )}
      {collab && campaign && (
        <CounterOfferModal
          isOpen={counterFormReason !== null}
          onClose={() => setCounterFormReason(null)}
          collab={collab}
          campaign={campaign}
          reason={counterFormReason ?? undefined}
        />
      )}

      {/* Input area — locked to the pre-filled options while a check-in awaits a choice */}
      {!canCompose ? (
        <Box
          borderTop="1px solid"
          borderColor={ff.line}
          px="16px"
          pt="14px"
          pb="calc(env(safe-area-inset-bottom) + 14px)"
          bg="white"
          flexShrink={0}
          textAlign="center"
        >
          <Text fontSize="14px" color={ff.text2}>
            You can reply here once the brand sends you a message.
          </Text>
        </Box>
      ) : checkInAwaitingChoice ? (
        <Box
          borderTop="1px solid"
          borderColor={ff.line}
          px="16px"
          pt="14px"
          pb="calc(env(safe-area-inset-bottom) + 14px)"
          bg="white"
          flexShrink={0}
          textAlign="center"
        >
          <Text fontSize="14px" color={ff.text2}>
            Please choose one of the options above to continue.
          </Text>
        </Box>
      ) : (
      <Box
        borderTop="1px solid"
        borderColor={ff.line}
        px="12px"
        pt="10px"
        pb="calc(env(safe-area-inset-bottom) + 10px)"
        bg="white"
        flexShrink={0}
      >
        {isUploading && (
          <Box mb="8px">
            <Progress
              value={uploadProgress}
              size="xs"
              bg={ff.line}
              borderRadius="full"
              isAnimated
              sx={{ "& > div": { bg: ff.ink } }}
            />
            <Text fontSize="11px" color={ff.text2} mt="2px" textAlign="right">
              Uploading: {Math.round(uploadProgress)}%
            </Text>
          </Box>
        )}

        <Flex
          as="form"
          onSubmit={async (e) => {
            e.preventDefault();
            await handleSendMessage();
          }}
          align="flex-end"
          gap="8px"
        >
          <input
            type="file"
            accept=".pdf, .jpeg, .jpg, .png, .mp4, .mov, .avi, .webm, video/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFileSelect(file);
            }}
            style={{ display: 'none' }}
            id="pdf-upload"
          />
          {/* File sends write to an existing thread; the first message on an invite must be text. */}
          {(threadExists || isMarketplaceThread) && (
          <label htmlFor="pdf-upload" style={{ cursor: 'pointer' }}>
            <Box
              as="span"
              display="flex"
              alignItems="center"
              justifyContent="center"
              w="44px"
              h="44px"
              borderRadius="full"
              bg={ff.grey}
              _hover={{ bg: ff.line }}
              transition="background 0.15s"
              mb="2px"
            >
              <AttachmentIcon boxSize={4} color={ff.ink} />
            </Box>
          </label>
          )}

          <Box flex={1} position="relative">
            <Box
              // Pill at one line (24px = half the 48px row); a fixed radius keeps a grown textarea readable.
              borderRadius="24px"
              border="1px solid"
              borderColor={ff.border}
              bg="white"
              _focusWithin={{ borderColor: ff.ink }}
              display="flex"
              alignItems="flex-end"
            >
              <Textarea
                ref={textareaRef}
                rows={1}
                placeholder="Message"
                value={message}
                onChange={(e) => {
                  if (!attachedFile) setMessage(e.target.value);
                }}
                minH="47px"
                maxH={{ base: "150px", md: "200px" }}
                resize="none"
                border="none"
                _focusVisible={{ boxShadow: "none" }}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    await handleSendMessage();
                  }
                }}
                disabled={!!attachedFile}
                px="14px"
                py="12px"
                fontSize="16px"
                lineHeight="1.375"
                color={ff.ink}
                _placeholder={{ color: ff.muted }}
              />
              {attachedFile && (
                <IconButton
                  aria-label="Remove file"
                  icon={<CloseIcon boxSize={2.5} />}
                  variant="ghost"
                  size="xs"
                  colorScheme="red"
                  onClick={resetMessageInput}
                  mb="10px"
                  mr="10px"
                />
              )}
            </Box>
          </Box>

          <IconButton
            aria-label="Send"
            icon={<FiSend size={17} />}
            type="submit"
            isRound
            w="44px"
            h="44px"
            minW="44px"
            bg={message.trim() || attachedFile ? ff.ink : ff.grey}
            color={message.trim() || attachedFile ? "white" : ff.muted}
            _hover={{
              bg: message.trim() || attachedFile ? ff.inkSoft : ff.line,
            }}
            _disabled={{ bg: ff.grey, color: ff.muted, cursor: "not-allowed" }}
            isDisabled={(!attachedFile && !message.trim()) || (!!attachedFile && !uploadedFileUrl)}
            isLoading={isUploading || isSending}
            mb="6px"
          />
        </Flex>
      </Box>
      )}
    </Box>
  );
};

export default MessagesPage;
